import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { CoverpathControllerInterface } from '../../../interfaces/controller/CoverpathControllerInterface';
import { Utils } from '../../../../../common/js/Utils';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { CommonUtils } from '../../../../../common/js/static/CommonUtils';
import { CoverpathPolicyStatus } from '../../../../../coverpath-client/public/js/components/account-center/policy-status/policy-status';
import { MMDirectPolicyStatus } from '../../../../../mm-direct/public/js/components/account-center/policy-status/policy-status';
import { Person } from '../../../../../common/js/customer/Person';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { WorkflowService } from '../../../services/workflow/workflow.service';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { TimeFrequency } from '../../../../../common/js/static/Product';
import type { CoverpathBasePolicy } from '../../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import type { CoverpathPolicy } from '../../../../classes/coverpath_subclasses/CoverpathPolicy';
import type { CasApplication } from '../../../../classes/coverpath_subclasses/CasApplication';
import type { MMDirectPolicy } from '../../../../classes/mmdirect_subclasses/MMDirectPolicy';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { CACPaymentData } from '../../../interfaces/CACPaymentData';
import type { Policy } from '../../../../../common/js/customer/Policy';

@Component({
  moduleId: module.id,
  selector: 'policy-overview-card',
  templateUrl: './policy-overview-card.component.html',
})
export class PolicyOverviewCardComponent implements OnInit {
  @Input() primaryPolicy: Policy;
  paPrimaryPolicyInfo: CACPaymentData;
  paSecondaryPolicyInfo: CACPaymentData;
  router: Router;
  controller: CoverpathControllerInterface;
  application: CasApplication;
  caseType: string;
  totalFaceAmount: string;
  premiumWithFrequency: string;
  primaryPolicyDescription: string;
  secondaryPolicyDescription: string;
  progressPercentage: number = 0;
  cardStatusTitles: string[];
  cardStatusSubTitles: string[];
  isCancelled: boolean;
  shouldShowPremium: boolean = true;
  insuredFullName: string;
  needsPaymentAuthorization: boolean;
  hasPaymentFailed: boolean;
  paymentFailureDate: Date;
  failedPaymentAmount: string;

  constructor(
    router: Router,
    protected amorphicService: AmorphicService,
    protected workflowService: WorkflowService,
    protected navigationService: NavigationService,
    protected accountCenterService: AccountCenterService
  ) {
    this.router = router;
  }

  ngOnInit(): void {
    this.setAllProperties();
  }

  async setAllProperties(): Promise<void> {
    const paPrimayPolicyData = this.accountCenterService.getPolicyDataFromPolicy(this.primaryPolicy.__id__ as string);
    this.paPrimaryPolicyInfo = paPrimayPolicyData.getCACPaymentData();

    const policyStatus = this.primaryPolicy.isCoverpathPolicy()
      ? new CoverpathPolicyStatus(
          this.amorphicService,
          this.primaryPolicy as CoverpathPolicy,
          this.accountCenterService
        )
      : new MMDirectPolicyStatus(this.amorphicService, this.primaryPolicy as MMDirectPolicy, this.accountCenterService);
    this.cardStatusTitles = policyStatus.getPolicyStatusTitle();
    this.cardStatusSubTitles = await policyStatus.getPolicyStatusSubTitle();
    this.caseType = this.getCaseType(this.primaryPolicy as CoverpathBasePolicy);
    const application: CasApplication = this.primaryPolicy.application as CasApplication;
    this.progressPercentage = application.progressPercentage;
    let totalFaceAmount = this.paPrimaryPolicyInfo.getCACFaceAmount();
    this.isCancelled = paPrimayPolicyData.getCACWasCanceledDeclinedRejected();
    this.primaryPolicyDescription = this.getPolicyDescription(this.primaryPolicy as CoverpathBasePolicy);
    if (application.isJointAppWithPendingAmendment()) {
      this.paSecondaryPolicyInfo = this.accountCenterService.getClientAccountCenterViewDataFromPolicy(
        application.secondaryPolicyWithPendingAmendment.__id__ as string
      );
      totalFaceAmount += this.paSecondaryPolicyInfo.getCACFaceAmount();
      this.secondaryPolicyDescription = this.getPolicyDescription(
        application.secondaryPolicyWithPendingAmendment as CoverpathBasePolicy
      );
    }

    this.totalFaceAmount = Utils.formatCurrency(totalFaceAmount, 0);
    const rawPremium =
      this.paPrimaryPolicyInfo.getCACTotalPremiumFromPolicyInfo() +
      (this.paSecondaryPolicyInfo ? this.paSecondaryPolicyInfo.getCACTotalPremiumFromPolicyInfo() : 0);
    this.shouldShowPremium = rawPremium > 0;
    const totalPremium = Utils.formatCurrency(rawPremium, 2);
    const frequency =
      TimeFrequency[
        CommonUtils.makePaymentFrequencySafeForPolicy(this.primaryPolicy.paymentFrequencyWithPendingAmendment())
      ];
    this.premiumWithFrequency = CoverpathUtils.getPremiumObjForPremiumAmount(
      this.primaryPolicy.getWorkflowAction(),
      totalPremium,
      this.primaryPolicy.premiumBreakdown.base,
      frequency,
      this.primaryPolicy.isTLIC(),
      false,
      this.primaryPolicy.isLifeBridge()
    ).premiumText;
    if (this.caseType === 'Quote') {
      const insuredPerson: Person = this.primaryPolicy.insured.person;
      this.cardStatusTitles = [
        `${insuredPerson.fullName}'s quote for MassMutual ${this.primaryPolicyDescription} ${
          application.isJointApp() ? ' & ' + this.secondaryPolicyDescription : ''
        } has ${this.totalFaceAmount} of coverage ${
          this.shouldShowPremium ? `and a premium of ${this.premiumWithFrequency}` : ``
        }`,
      ];
    }

    this.insuredFullName = paPrimayPolicyData.getCACInsuredFullName();
    this.needsPaymentAuthorization = this.paPrimaryPolicyInfo.getCACNeedsAuthorizationWithinOrPast();
    this.hasPaymentFailed = application.isJointAppWithPendingAmendment()
      ? this.paPrimaryPolicyInfo.getCACHasPaymentFailed() || this.paSecondaryPolicyInfo.getCACHasPaymentFailed()
      : this.paPrimaryPolicyInfo.getCACHasPaymentFailed();
  }

  getPolicyDescription(policy: CoverpathBasePolicy) {
    return CoverpathUtils.getCACProductDescriptionFromPolicy(policy);
  }

  getCaseType(policy: CoverpathBasePolicy) {
    const subState =
      policy.application.primaryPolicy.futureWorkflowSubState || policy.application.primaryPolicy.workflowSubState;
    const isConversionApp =
      policy.application.primaryPolicy.convertedFrom &&
      this.amorphicService.controller.enableConversionAutomation &&
      subState === 'Conversion started';
    if (isConversionApp) {
      return `Conversion`;
    }
    if (!policy.application.primaryPolicy.submittedAt) {
      return `Quote`;
    }

    const workflowAction = `${this.workflowService.getWorkflowStateFromPolicy(
      policy
    )}:${this.workflowService.getWorkflowSubStateFromPolicy(policy)}`;
    if (
      CoverpathUtils.isInForce(workflowAction) ||
      CoverpathUtils.isInFreeLook(workflowAction) ||
      CoverpathUtils.isCanceled(workflowAction)
    ) {
      return `Policy`;
    }

    return `Application`;
  }

  goToCase(policy: Policy) {
    this.amorphicService.controller.customer.application = policy.application as CasApplication;
    if (this.getCaseType(policy as CoverpathBasePolicy) === 'Quote') {
      this.router.navigate(['application-form']).then((success) => {
        if (success) {
          // When routing from policy overview to application form, take user to first section by default
          this.navigationService.activeApplicationSection$.next(0);
        } else {
          this.amorphicService.amorphicLogError(
            'PolicyOverviewCardComponent',
            'goToCase',
            'Failed to return to application form page',
            Utils.extractIds(this.amorphicService.controller.customer)
          );
        }
      });
    } else {
      this.router.navigate(['account-center-detailed-view'], {
        // TODO: do we need to update this to case-details?
        queryParams: { applicationNumber: policy.application.applicationNumber },
      });
    }
  }
}
